import { PencilIcon, ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Switch,
} from '@nextui-org/react';
import { CheckIcon, CloseIcon } from '@nextui-org/shared-icons';
import React, { useState, Key } from 'react';
import { toast } from 'react-toastify';

import { ConfigItem, CategoryItem } from '@common/types';

import { InputSearchUI } from '../../components/common/InputSearch.ui';
import { NoteIconUI } from '../../components/common/NoteIcon.ui';
import { TerminalIconUI } from '../../components/common/TerminalIcon.ui';
import { TypographyUI } from '../../components/common/Typography.ui';
import { UnifiedNavigationUI } from '../../components/common/UnifiedNavigation.ui';
import { UserDropdown } from '../../components/UserDropdown';
import { tsrestClient } from '../../provider/tsrest';
import { useAuthStatus } from '../../utils/useAuthStatus';

// Reusable table component
const ConfigTable = ({
  configs,
  title,
  subtitle,
  editingKey,
  editingConfigs,
  onConfigChange,
  onStartEditing,
  onSaveEditing,
  onCancelEditing,
  onResetConfig,
  isConfigChanged,
  updateConfigMutation,
  resetConfigMutation,
  hasBottomMargin = true,
}: {
  configs: Array<[string, ConfigItem]>;
  title: string;
  subtitle?: string;
  editingKey: string | null;
  editingConfigs: Record<string, string>;
  onConfigChange: (key: string, value: string) => void;
  onStartEditing: (key: string) => void;
  onSaveEditing: (key: string) => void;
  onCancelEditing: (key: string) => void;
  onResetConfig: (key: string) => void;
  isConfigChanged: (key: string) => boolean;
  updateConfigMutation: ReturnType<typeof tsrestClient.config.update.useMutation>;
  resetConfigMutation: ReturnType<typeof tsrestClient.config.reset.useMutation>;
  hasBottomMargin?: boolean;
}) => {
  // Convert configs array to items format for proper striping
  const configItems = configs.map(([key, config]) => ({
    key,
    config,
  }));

  const columns = [
    { key: 'configuration', label: 'Configuration', width: 400 },
    { key: 'value', label: 'Value', width: 300 },
    { key: 'status', label: 'Status', width: 120 },
    { key: 'actions', label: 'Actions', width: 100 },
  ];

  const renderCell = (item: { key: string; config: ConfigItem }, columnKey: Key) => {
    const { key, config } = item;

    switch (columnKey) {
      case 'configuration':
        return (
          <div className='flex flex-col gap-1'>
            <TypographyUI className='text-sm font-semibold' color='blue-gray'>
              {key}
            </TypographyUI>
            <TypographyUI className='text-xs text-gray-500 leading-relaxed'>{config.description}</TypographyUI>
          </div>
        );
      case 'value':
        return (
          <div className='h-8 flex items-center'>
            {editingKey === key ? (
              <div className='w-full'>
                {config.type === 'boolean' ? (
                  <div className='flex items-center gap-2'>
                    <Switch
                      size='sm'
                      isSelected={editingConfigs[key] === 'true'}
                      onValueChange={value => onConfigChange(key, value.toString())}
                    />
                    <TypographyUI className='text-sm'>
                      {editingConfigs[key] === 'true' ? 'Enabled' : 'Disabled'}
                    </TypographyUI>
                  </div>
                ) : config.type === 'number' ? (
                  <Input
                    size='sm'
                    type='number'
                    step='any'
                    value={editingConfigs[key] || ''}
                    onChange={e => onConfigChange(key, e.target.value)}
                    className='w-full'
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onSaveEditing(key);
                      } else if (e.key === 'Escape') {
                        onCancelEditing(key);
                      }
                    }}
                  />
                ) : config.type === 'second' ? (
                  <Input
                    size='sm'
                    type='number'
                    value={editingConfigs[key] || ''}
                    onChange={e => onConfigChange(key, e.target.value)}
                    className='w-full'
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onSaveEditing(key);
                      } else if (e.key === 'Escape') {
                        onCancelEditing(key);
                      }
                    }}
                  />
                ) : (
                  <Input
                    size='sm'
                    type={config.type === 'password' ? 'password' : 'text'}
                    value={editingConfigs[key] || ''}
                    onChange={e => onConfigChange(key, e.target.value)}
                    className='w-full'
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onSaveEditing(key);
                      } else if (e.key === 'Escape') {
                        onCancelEditing(key);
                      }
                    }}
                  />
                )}
              </div>
            ) : (
              <div className='w-full' title={config.isSecret ? '••••••••' : config.value}>
                <TypographyUI className='text-sm truncate max-w-full'>
                  {config.isSecret
                    ? '•'.repeat(config.value.length)
                    : config.type === 'boolean'
                      ? config.value === 'true'
                        ? 'Enabled'
                        : 'Disabled'
                      : config.value}
                </TypographyUI>
              </div>
            )}
          </div>
        );
      case 'status':
        return (
          <div className='flex items-center gap-1'>
            {isConfigChanged(key) ? (
              <Chip size='sm' color='warning' variant='flat'>
                Changed
              </Chip>
            ) : config.isDefault ? (
              <Chip size='sm' color='default' variant='flat'>
                Default
              </Chip>
            ) : (
              <Chip size='sm' color='success' variant='flat'>
                Modified
              </Chip>
            )}
          </div>
        );
      case 'actions':
        return (
          <div className='flex items-center gap-1'>
            {editingKey === key ? (
              <>
                <Tooltip content='Save'>
                  <Button
                    isIconOnly
                    size='sm'
                    color='success'
                    variant='light'
                    onPress={() => onSaveEditing(key)}
                    isLoading={updateConfigMutation.isLoading}
                  >
                    <CheckIcon className='h-3 w-3' />
                  </Button>
                </Tooltip>
                <Tooltip content='Cancel'>
                  <Button isIconOnly size='sm' color='danger' variant='light' onPress={() => onCancelEditing(key)}>
                    <CloseIcon className='h-3 w-3' />
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip content='Edit'>
                  <Button isIconOnly size='sm' color='primary' variant='light' onPress={() => onStartEditing(key)}>
                    <PencilIcon className='h-3 w-3' />
                  </Button>
                </Tooltip>
                {!config.isDefault && !config.isSecret && (
                  <Tooltip content='Reset to Default'>
                    <Button
                      isIconOnly
                      size='sm'
                      color='warning'
                      variant='light'
                      onPress={() => onResetConfig(key)}
                      isLoading={resetConfigMutation.isLoading}
                    >
                      <ArrowPathIcon className='h-3 w-3' />
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className={`w-full ${hasBottomMargin ? 'mb-8' : ''}`} shadow='sm'>
      <CardHeader className='flex-col items-start gap-1 pb-2 px-4'>
        <div className='flex items-center gap-2'>
          <TypographyUI variant='h6' color='blue-gray' className='font-semibold'>
            {title}
          </TypographyUI>
          <Chip size='sm' variant='flat' color='primary'>
            {configs.length}
          </Chip>
        </div>
        {subtitle && <TypographyUI className='text-xs text-gray-500'>{subtitle}</TypographyUI>}
      </CardHeader>
      <CardBody className='p-4'>
        <Table
          removeWrapper
          isStriped
          isHeaderSticky
          aria-label={`${title} configuration settings`}
          className='relative z-10'
        >
          <TableHeader columns={columns}>
            {column => (
              <TableColumn
                key={column.key}
                align='start'
                width={column.width}
                className={`w-[${column.width}px] min-w-[${column.width}px] max-w-[${column.width}px]`}
              >
                {column.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={configItems} emptyContent='No configurations found'>
            {item => (
              <TableRow key={item.key}>
                {columnKey => (
                  <TableCell
                    className={
                      columnKey === 'configuration'
                        ? 'w-[400px] min-w-[400px] max-w-[400px]'
                        : columnKey === 'value'
                          ? 'w-[300px] min-w-[300px] max-w-[300px]'
                          : columnKey === 'status'
                            ? 'w-[120px] min-w-[120px] max-w-[120px]'
                            : columnKey === 'actions'
                              ? 'w-[100px] min-w-[100px] max-w-[100px]'
                              : ''
                    }
                  >
                    {renderCell(item, columnKey)}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
};

export const Manage = () => {
  const { isSuperAdmin } = useAuthStatus();
  const [editingConfigs, setEditingConfigs] = useState<Record<string, string>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  // Fetch all configs
  const configsQuery = tsrestClient.config.getAll.useQuery(['allConfigs'], {
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });

  // Fetch category descriptions
  const categoriesQuery = tsrestClient.config.categories.useQuery(['configCategories'], {
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });

  // Update config mutation
  const updateConfigMutation = tsrestClient.config.update.useMutation();

  // Reset config mutation
  const resetConfigMutation = tsrestClient.config.reset.useMutation();

  const handleConfigChange = (key: string, value: string) => {
    setEditingConfigs(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const isConfigChanged = (key: string) => {
    const originalValue = configsQuery.data?.body.configs[key]?.value;
    const editedValue = editingConfigs[key];
    return editedValue !== undefined && editedValue !== originalValue;
  };

  const handleUpdateConfig = async (key: string) => {
    const value = editingConfigs[key];
    if (value === undefined) return;

    try {
      await updateConfigMutation.mutateAsync({
        params: { key },
        body: { value },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      setEditingConfigs(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      configsQuery.refetch();
    } catch (error) {
      // Handle error silently or show toast
    }
  };

  const handleResetConfig = async (key: string) => {
    try {
      await resetConfigMutation.mutateAsync({
        params: { key },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      setEditingConfigs(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      configsQuery.refetch();
    } catch (error) {
      // Handle error silently or show toast
    }
  };

  const startEditing = (key: string) => {
    setEditingKey(key);
    if (editingConfigs[key] === undefined) {
      const config = configsQuery.data?.body.configs[key];
      setEditingConfigs(prev => ({
        ...prev,
        [key]: config?.isSecret ? '' : config?.value || '',
      }));
    }
  };

  const cancelEditing = (key: string) => {
    setEditingKey(null);
    setEditingConfigs(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  };

  const saveEditing = async (key: string) => {
    await handleUpdateConfig(key);
    setEditingKey(null);
  };

  const handleDownloadBackup = async () => {
    try {
      const response = await fetch('/api/v1/system/backup/download', {
        method: 'GET',
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Get filename from Content-Disposition header or generate one
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `monitors-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
        if (contentDisposition) {
          // Try RFC 5987 encoded filename first (filename*=UTF-8''...)
          let filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
          if (filenameMatch) {
            try {
              filename = decodeURIComponent(filenameMatch[1]);
            } catch (e) {
              // If decoding fails, fall back to regular filename
            }
          } else {
            // Try regular filename with quotes
            filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
            if (filenameMatch) {
              filename = filenameMatch[1];
            } else {
              // Try filename without quotes
              filenameMatch = contentDisposition.match(/filename=([^;]+)/);
              if (filenameMatch) {
                filename = filenameMatch[1].trim();
              }
            }
          }
        }
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast('Backup downloaded successfully', { type: 'success' });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to download backup' }));
        toast(`Failed to download backup: ${errorData.message}`, { type: 'error' });
      }
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast('Failed to download backup', { type: 'error' });
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className='flex w-full h-screen items-center justify-center'>
        <TypographyUI variant='h5' color='red'>
          Access Denied: Super Admin Required
        </TypographyUI>
      </div>
    );
  }

  // Filter configs based on search value
  const filteredConfigs = Object.entries(configsQuery.data?.body.configs || {}).filter(([key, config]) => {
    if (!searchValue.trim()) return true;
    const searchLower = searchValue.toLowerCase();
    return (
      key.toLowerCase().includes(searchLower) ||
      config.description.toLowerCase().includes(searchLower) ||
      config.value.toLowerCase().includes(searchLower) ||
      config.category.toLowerCase().includes(searchLower)
    );
  });

  // Group configs by category
  const configsByCategory: Record<string, Array<[string, ConfigItem]>> = {};
  Object.entries(configsQuery.data?.body.configs || {}).forEach(([key, config]) => {
    const category = config.category || 'Other';
    if (!configsByCategory[category]) {
      configsByCategory[category] = [];
    }
    configsByCategory[category].push([key, config]);
  });

  // Group filtered configs by category
  const filteredConfigsByCategory: Record<string, Array<[string, ConfigItem]>> = {};
  filteredConfigs.forEach(([key, config]) => {
    const category = config.category || 'Other';
    if (!filteredConfigsByCategory[category]) {
      filteredConfigsByCategory[category] = [];
    }
    filteredConfigsByCategory[category].push([key, config]);
  });

  return (
    <>
      {/* Navigation Header */}
      <div className='bg-white border-b border-gray-200 shadow-sm mb-6'>
        <div className='py-2'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-6'>
              <UnifiedNavigationUI />
              <div>
                <TypographyUI className='text-lg font-semibold' color='blue-gray'>
                  System Configuration
                </TypographyUI>
                <TypographyUI className='text-sm' color='gray'>
                  Manage system configuration settings
                </TypographyUI>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <TerminalIconUI />
              <NoteIconUI />
              <Tooltip content='Download server backup (database and config)'>
                <Button
                  variant='bordered'
                  color='primary'
                  size='sm'
                  startContent={<ArrowDownTrayIcon className='h-4 w-4' />}
                  onPress={handleDownloadBackup}
                >
                  Download Backup
                </Button>
              </Tooltip>
              <InputSearchUI onSearchChange={setSearchValue} placeholder='Search Configurations' />
              <UserDropdown />
            </div>
          </div>
        </div>
      </div>

      <div className='flex justify-center'>
        <div className='w-full max-w-7xl px-6'>
          {configsQuery.isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <Spinner size='lg' />
            </div>
          ) : (
            <>
              {searchValue.trim() ? (
                // Single table for search results
                <ConfigTable
                  configs={filteredConfigs}
                  title='Search Results'
                  subtitle={`Showing configurations matching "${searchValue}"`}
                  editingKey={editingKey}
                  editingConfigs={editingConfigs}
                  onConfigChange={handleConfigChange}
                  onStartEditing={startEditing}
                  onSaveEditing={saveEditing}
                  onCancelEditing={cancelEditing}
                  onResetConfig={handleResetConfig}
                  isConfigChanged={isConfigChanged}
                  updateConfigMutation={updateConfigMutation}
                  resetConfigMutation={resetConfigMutation}
                  hasBottomMargin={false} // No bottom margin for search results
                />
              ) : (
                // Multiple tables by category when not searching
                <div className='flex flex-col gap-6'>
                  {Object.entries(configsByCategory).map(([category, configs], index, array) => (
                    <ConfigTable
                      key={category}
                      configs={configs}
                      title={category}
                      subtitle={
                        categoriesQuery.data?.body.categories.find(
                          cat => cat.name.toLowerCase() === category.toLowerCase()
                        )?.description || `${category} configuration settings`
                      }
                      editingKey={editingKey}
                      editingConfigs={editingConfigs}
                      onConfigChange={handleConfigChange}
                      onStartEditing={startEditing}
                      onSaveEditing={saveEditing}
                      onCancelEditing={cancelEditing}
                      onResetConfig={handleResetConfig}
                      isConfigChanged={isConfigChanged}
                      updateConfigMutation={updateConfigMutation}
                      resetConfigMutation={resetConfigMutation}
                      hasBottomMargin={index === array.length - 1} // Apply bottom margin to the last table
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
